import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchStudentById, updateStudent } from '../../services/studentService';
import Layout from '../layout/Layout';
import './StudentProfile.css';
import './StudentProfileEdit.css';
import { industryCategories, findIndustryCategory } from '../../data/mentorFormOptions';

const StudentProfileEdit = () => {
  const [student, setStudent] = useState(null);
  const [formData, setFormData] = useState({
    氏名: '',
    学年: ''
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [industryCategory, setIndustryCategory] = useState('');
  const [industrySubcategory, setIndustrySubcategory] = useState('');

  useEffect(() => {
    const getStudentProfile = async () => {
      if (!currentUser) return;
      
      try {
        const studentData = await fetchStudentById(currentUser.uid);
        setStudent(studentData);
        // フォームに現在の値を設定
        setFormData({
          氏名: studentData.氏名 || '',
          学年: studentData.学年 || ''
        });

        // 志望業界の設定（既存データがある場合）
        if (studentData.志望業界) {
          // 配列の場合は最初の要素を使用、文字列の場合はそのまま使用
          const industryData = Array.isArray(studentData.志望業界) 
            ? studentData.志望業界[0] 
            : studentData.志望業界;
          
          const { foundCategory, foundSubcategory } = findIndustryCategory(industryData);
          setIndustryCategory(foundCategory);
          setIndustrySubcategory(foundSubcategory);
        }
        setLoading(false);
      } catch (err) {
        console.error('Error fetching student profile:', err);
        setError('プロフィール情報の取得に失敗しました。');
        setLoading(false);
      }
    };

    getStudentProfile();
  }, [currentUser]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 業界カテゴリ変更時の処理
  const handleIndustryCategoryChange = (e) => {
    const category = e.target.value;
    setIndustryCategory(category);
    setIndustrySubcategory(''); // サブカテゴリをリセット
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // 業界情報を組み合わせて保存
      const updateData = {
        ...formData,
      };
      
      // 業界が選択されている場合のみ追加
      if (industryCategory) {
        updateData.志望業界 = industrySubcategory 
          ? `${industryCategory} - ${industrySubcategory}`
          : industryCategory;
      }

      await updateStudent(currentUser.uid, updateData);
      setSuccess(true);
      setTimeout(() => {
        navigate('/student-profile');
      }, 1500);
    } catch (err) {
      console.error('Error updating student profile:', err);
      setError('プロフィールの更新に失敗しました。もう一度お試しください。');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/student-profile');
  };

  if (loading) return <div className="loading">読み込み中...</div>;
  if (!student) return <div className="error">プロフィール情報が見つかりません。</div>;

  return (
    <Layout>
      <div className="profile-container">
        <h2 className="profile-heading">プロフィール編集</h2>
        
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">プロフィールを更新しました！</div>}
        
        <form onSubmit={handleSubmit} className="profile-edit-form">
          <div className="form-section">
            <h3>基本情報</h3>
            
            <div className="form-group">
              <label htmlFor="氏名">氏名 *</label>
              <input
                type="text"
                id="氏名"
                name="氏名"
                value={formData.氏名}
                onChange={handleInputChange}
                required
                placeholder="山田 太郎"
              />
            </div>

            <div className="form-group">
              <label htmlFor="学年">学年 *</label>
              <select
                id="学年"
                name="学年"
                value={formData.学年}
                onChange={handleInputChange}
                required
              >
                <option value="">選択してください</option>
                <option value="1年">1年</option>
                <option value="2年">2年</option>
                <option value="3年">3年</option>
                <option value="4年">4年</option>
                <option value="5年">5年</option>
                <option value="6年">6年</option>
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="industryCategory">志望業界（大分類）</label>
              <select
                id="industryCategory"
                value={industryCategory}
                onChange={handleIndustryCategoryChange}
              >
                <option value="">選択してください</option>
                {Object.keys(industryCategories).map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="industrySubcategory">志望業界（詳細）</label>
              <select
                id="industrySubcategory"
                value={industrySubcategory}
                onChange={(e) => setIndustrySubcategory(e.target.value)}
                disabled={!industryCategory}
              >
                <option value="">
                  {industryCategory ? "選択してください" : "まず業界カテゴリを選択してください"}
                </option>
                {industryCategory &&
                  industryCategories[industryCategory].map((subcategory) => (
                    <option key={subcategory} value={subcategory}>
                      {subcategory}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          <div className="form-buttons">
            <button 
              type="button" 
              onClick={handleCancel}
              className="cancel-button"
              disabled={saving}
            >
              キャンセル
            </button>
            <button 
              type="submit" 
              className="save-button"
              disabled={saving}
            >
              {saving ? '保存中...' : '保存'}
            </button>
          </div>
        </form>
      </div>
    </Layout>
  );
};

export default StudentProfileEdit;